import { addBusinessDays, addDays, differenceInDays, format } from "date-fns";

import { ToneOfVoiceCapturePayload } from "@/pages/Private/pages/Messages/components/GenerateBar/types";

export interface DraftSender {
	userId: number;
	type: "email" | "linkedin";
	label: string;
	emailId?: number;
	emailAddress?: string;
}

export interface DraftRecipient {
	type: "email" | "linkedin";
	label: string;
	value: string;
}

export interface DraftTiming {
	id: string;
	label: string;
	shortLabel: string;
	value: string | Date;
	scheduledInDays?: number;
}

export interface Draft {
	id: number;
	content: string;
	subject?: string;
	from: DraftSender;
	to: DraftRecipient;
	timing: DraftTiming;
	cc?: string[];
	bcc?: string[];
	showCcBcc?: boolean;
	isInviteNote?: boolean;
	aiOrigin?: ToneOfVoiceCapturePayload;
	/** Reply Agent's reasoning for this draft (why this message/timing/channel) — shown on the
	 * "Why this?" hover. Only set for reply-agent prefilled drafts (reply1/2/3). */
	reasoning?: string;
	/** Email only: the user's Include signature choice. Unset follows the company's default (PREC-4401). */
	includeSignature?: boolean;
}

const getNextBusinessDay = (days: number): string => {
	const nextBusinessDay = addBusinessDays(new Date(), days);

	return format(nextBusinessDay, "EEEE");
};

export const getTimingPresets = (): DraftTiming[] => [
	{
		id: "now",
		label: "Now",
		shortLabel: "Now",
		value: "now",
		scheduledInDays: 0,
	},
	{
		id: "tomorrow",
		label: "Tomorrow",
		shortLabel: format(addDays(new Date(), 1), "dd.MM"),
		value: "tomorrow",
		scheduledInDays: 1,
	},
	{
		id: "3days",
		label: `In 3 business days (${getNextBusinessDay(3)})`,
		shortLabel: format(addBusinessDays(new Date(), 3), "dd.MM"),
		value: "3days",
		scheduledInDays: differenceInDays(addBusinessDays(new Date(), 3), new Date()),
	},
	{
		id: "1week",
		label: `In 1 week (${format(addDays(new Date(), 7), "dd. MMMM")})`,
		shortLabel: format(addDays(new Date(), 7), "dd.MM"),
		value: "1week",
		scheduledInDays: 7,
	},
	{
		id: "2weeks",
		label: `In 2 weeks (${format(addDays(new Date(), 14), "dd. MMMM")})`,
		shortLabel: format(addDays(new Date(), 14), "dd.MM"),
		value: "2weeks",
		scheduledInDays: 14,
	},
	{
		id: "1month",
		label: `In 1 month (${format(addDays(new Date(), 30), "dd. MMMM")})`,
		shortLabel: format(addDays(new Date(), 30), "dd.MM"),
		value: "1month",
		scheduledInDays: 30,
	},
	{
		id: "3months",
		label: `In 3 months (${format(addDays(new Date(), 90), "dd. MMMM")})`,
		shortLabel: format(addDays(new Date(), 90), "dd.MM"),
		value: "3months",
		scheduledInDays: 90,
	},
	{
		id: "custom",
		label: "Custom date",
		shortLabel: "Custom",
		value: "custom",
	},
];

/**
 * Converts a scheduledInDays number to the matching DraftTiming preset,
 * or creates a custom timing if no preset matches.
 */
export const timingFromDays = (days: number | null | undefined): DraftTiming => {
	if (days === null || days === undefined) {
		return getTimingPresets()[0];
	} // "Now"

	const presets = getTimingPresets();
	const match = presets.find((p) => p.scheduledInDays === days);

	if (match) {
		return match;
	}

	// No preset match → build a custom timing entry
	const targetDate = addDays(new Date(), days);

	return {
		id: "custom",
		label: `In ${days} days (${format(targetDate, "dd. MMMM")})`,
		shortLabel: format(targetDate, "dd.MM"),
		value: targetDate,
		scheduledInDays: days,
	};
};

export const createDefaultDraft = (
	id: number,
	defaultFrom?: DraftSender,
	defaultTo?: DraftRecipient,
	defaultContent?: string,
	defaultTiming?: DraftTiming
): Draft => ({
	id,
	content: defaultContent ?? "",
	from: defaultFrom ?? {
		userId: 0,
		type: "email",
		label: "Select sender",
	},
	to: defaultTo ?? {
		type: "email",
		label: "Select recipient",
		value: "",
	},
	timing: defaultTiming ?? getTimingPresets()[0],
});
